import React, {ComponentType} from 'react';

import {Location} from 'history';
import {DefaultRootState, useSelector} from 'react-redux';
import {withRouter, WithRouterProps} from 'react-router';

import {LinkPaginationRTK} from 'app/components/general/LinkPaginationRTK';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {StyledListDiv} from 'app/general/bootstrap/utils';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {LoadingList, LoadingListProps} from 'app/general/loading/LoadingList';

import type {QueryDefinition} from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import type {UseQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type {ListResponseType} from 'sweet-types';

const wrapInNoResultsIfString = (content: JSX.Element | string | null) =>
  typeof content === 'string' ? <NoResultsMessage head={content} /> : content;

type UserRoleType = {
  isManager: boolean;
  isSuperManager: boolean;
  isTeamMember: boolean;
};

export interface GenericPaginatedListCommonProps<
  PR,
  CSP extends Record<string, unknown> = Record<string, unknown>
> {
  noItemsMessage: (
    perms: UserRoleType,
    MessageComponent: typeof NoResultsMessage,
    props: CSP | Record<string, unknown>
  ) => JSX.Element | string | null;
  paramsBuilder: (router: {location: Location<any>; params: any}) => PR;
  pageParamName?: string;
  fetchPageName?: string;
  loadingListProps?: LoadingListProps;
}

type ItemType = {
  id?: string | number;
  url?: string;
  html_url?: string;
};

interface Props<IT, PR, CSP extends Record<string, unknown>>
  extends GenericPaginatedListCommonProps<PR, CSP>,
    WithRouterProps {
  ItemComponent: ComponentType<
    CSP & {
      object: IT;
    }
  >;
  useQuery: UseQuery<QueryDefinition<any, any, any, any, any>>;
  children?: (
    response: ListResponseType<IT>,
    list: JSX.Element | null
  ) => JSX.Element | null;
  borderTop?: boolean;
  customProps?: (state: DefaultRootState) => CSP;
  resultsFormatter?: (results: Array<IT>) => Array<IT>;
  WrapperComponent?: (props: {children: React.ReactNode}) => JSX.Element;
  itemsCounterText?: (count: number) => React.ReactNode;
}

const BaseGenericPaginatedList = withRouter(
  <IT extends ItemType, PR, CSP extends Record<string, unknown>>({
    ItemComponent,
    useQuery,
    paramsBuilder,
    children,
    noItemsMessage,
    borderTop,
    loadingListProps = {},
    pageParamName = 'page',
    fetchPageName = 'page',
    customProps,
    resultsFormatter,
    WrapperComponent,
    location,
    params,
    itemsCounterText,
  }: Props<IT, PR, CSP>): JSX.Element | null => {
    const stateProps: {
      userPerms: UserRoleType;
      customProps: CSP;
    } = useSelector((state) => {
      const {isTeamMember, isManager, isSuperManager} = getCurrentUserPerms(
        CurrentUserSelector(state).data
      );
      return {
        userPerms: {
          isManager,
          isSuperManager,
          isTeamMember,
        },
        customProps:
          customProps != null ? customProps(state) : Object.freeze({} as CSP),
      };
    });
    const hookParams = {
      [fetchPageName]: location.query[pageParamName] as string,
      ...paramsBuilder({
        location,
        params,
      }),
    };
    const {data, isLoading} = useQuery(hookParams);
    const results =
      resultsFormatter != null
        ? resultsFormatter(data?.results)
        : data?.results ?? [];
    const formattedResults: React.ReactNode = (
      <>
        {itemsCounterText != null ? (
          <DashboardItemsCounter text={itemsCounterText(data?.count)} />
        ) : null}
        {data?.results?.map((object) => (
          <ItemComponent
            key={object.url || object.html_url || object.id}
            {...stateProps.customProps}
            object={object}
          />
        ))}
      </>
    );

    const list = isLoading ? (
      <LoadingList {...loadingListProps} />
    ) : results.length === 0 ? (
      wrapInNoResultsIfString(
        noItemsMessage(
          stateProps.userPerms,
          NoResultsMessage,
          stateProps.customProps
        )
      )
    ) : (
      <StyledListDiv className={borderTop === true ? 'border-top' : ''}>
        {WrapperComponent != null ? (
          <WrapperComponent>{formattedResults}</WrapperComponent>
        ) : (
          formattedResults
        )}
        <div className="mt-4">
          <LinkPaginationRTK
            isLoading={isLoading}
            response={data}
            page={location.query[pageParamName]}
            pageParamName={pageParamName}
          />
        </div>
      </StyledListDiv>
    );
    return children != null ? children(data, list) : list;
  }
);

/*
  all this dancing is required to keep the generic types after using withRouter
  as the return type of withRouter isn't generic
*/
export const GenericPaginatedListRTK = BaseGenericPaginatedList as <
  IT extends ItemType,
  PR,
  CSP extends Record<string, unknown>
>(
  props: Omit<Props<IT, PR, CSP>, keyof WithRouterProps>
) => JSX.Element;
